import React, { useEffect, useState } from "react";
import OrderIcon from "../../../../assets/images/paymentDone/paymentDone.png";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { FaFacebookF, FaInstagram, FaLinkedinIn } from "react-icons/fa";
import "./style.css";
import { useDispatch } from "react-redux";
import { clearCart } from "../../../../redux/slices/cartSlice";
import { CartApiService } from "../../../service/cartApiService";


const safeGet = (obj, path, fallback = "") => {
    try {
        return path.split('.').reduce((o, k) => (o && o[k] !== undefined ? o[k] : undefined), obj) ?? fallback;
    } catch {
        return fallback;
    }
};

const formatINR = (n) => {
    if (n === null || n === undefined || isNaN(Number(n))) return "₹0";
    return `₹${Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

const truncate = (str = "", n = 20) => {
    if (str.length <= n) return str;
    return str.slice(0, n) + "...";
};

export default function ThankYouPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useDispatch();
    const [searchParams] = useSearchParams();
    const [ccavenueOrder, setCcavenueOrder] = useState(null);

    useEffect(() => {
        dispatch(clearCart());
    }, []);

    // CCAvenue redirect: fetch order details from query params
    const qsOrderId = searchParams.get("orderId") || "";
    const qsStatus = searchParams.get("status") || "";

    useEffect(() => {
        if (!location.state?.order && qsOrderId) {
            CartApiService.ccavenueStatus(qsOrderId)
                .then((res) => {
                    if (res?.status?.code === 200 && res.data) {
                        setCcavenueOrder(res.data);
                    }
                })
                .catch(() => { });
        }
    }, [qsOrderId]);

    const paymentState = location.state || {};

    const order = paymentState.order || {};
    const paymentMethod = paymentState.paymentMethod
        || (qsOrderId ? "ccavenue" : "—");
    const cartTotals = paymentState.cartTotals || null;
    const seller = paymentState.seller || null;

    // Cart items used to show product thumbnails & details (limit 3 with vertical scroll)
    const cartItems = Array.isArray(paymentState.cartItems) ? paymentState.cartItems : [];

    const orderId =
        order.id ||
        order.orderId ||
        order._id ||
        safeGet(order, "data.orderId") ||
        ccavenueOrder?.orderId ||
        qsOrderId ||
        "—";

    const products = cartItems.flatMap((cartItem) => {
        const product = cartItem.product || {};
        const variants = Array.isArray(cartItem.variants) ? cartItem.variants : [];
        const firstVariant = variants[0] || {};
        const quantity = variants.reduce((sum, v) => sum + (v.quantity || 0), 0);
        const imageUrl =
            firstVariant?.skuAttributes?.[0]?.skuImageUrl ||
            firstVariant?.image ||
            product?.imgUrl?.[0] ||
            "";
        const name = product?.title || product?.name || "Product";
        const perPiece =
            (firstVariant?.pricingBreakdown?.FBP &&
                parseFloat(firstVariant.pricingBreakdown.FBP)) ||
            parseFloat(firstVariant?.price || 0);
        const amount = perPiece * (quantity || 0);

        return [
            {
                id: `${cartItem._id || name}`,
                imageUrl,
                name,
                quantity: quantity || 0,
                amount: isFinite(amount) ? amount : 0,
            },
        ];
    });

    // Seller block values (if available)
    const sellerName =
        safeGet(seller, "name") ||
        safeGet(seller, "businessName") ||
        safeGet(seller, "storeName") ||
        "";
    const sellerEmail = safeGet(seller, "email", "");
    const sellerPhone = safeGet(seller, "phone", "");

    // Totals block
    const subtotal = cartTotals?.totalAmount ?? null;
    const totalGst = cartTotals?.totalGst ?? null;
    const discount = 0;
    const grandTotal =
        (cartTotals?.grandTotal !== undefined && cartTotals?.grandTotal !== null)
            ? cartTotals.grandTotal
            : products.reduce((s, p) => s + (p.amount || 0), 0);

    const isBankTransfer = paymentMethod === 'bank_transfer';

    return (
        <div className="thankyou_container">
            <div className="thankyou_card">

                {/* Bank Transfer pending notice banner */}
                {isBankTransfer && (
                    <div style={{
                        background: '#fffbeb',
                        border: '1px solid #fbbf24',
                        borderRadius: '10px',
                        padding: '14px 20px',
                        margin: '0 0 16px 0',
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: '12px',
                    }}>
                        <span style={{ fontSize: '22px', flexShrink: 0 }}>⏳</span>
                        <div>
                            <p style={{ margin: '0 0 4px 0', fontWeight: '700', color: '#92400e', fontSize: '15px' }}>
                                Awaiting Payment Verification
                            </p>
                            <p style={{ margin: 0, color: '#78350f', fontSize: '13px', lineHeight: '1.5' }}>
                                Your order has been placed and is on hold until our team verifies your bank transfer. This usually takes a few hours on business days. You will receive a confirmation email once verified.
                            </p>
                        </div>
                    </div>
                )}

                <div className="thankyou_content_wrapper">

                    <div className="thankyou_left_column">

                        <div className="thankyou_image_box">
                            <img src={OrderIcon} alt="order-icon" className="thankyou_icon" />
                        </div>

                        <h1 className="thankyou_title">
                            {isBankTransfer ? 'Order Placed — Pending Confirmation' : 'Procurement Request Received!'}
                        </h1>
                        <p className="thankyou_subtitle">
                            {isBankTransfer
                                ? 'Thank you! Your order is placed and awaiting bank transfer payment confirmation from our team.'
                                : 'Your procurement request has been received and is under review. We will confirm your order shortly.'
                            }
                        </p>

                        {(sellerName || sellerEmail || sellerPhone) && (
                            <div className="seller_box">
                                <h4>Seller Details</h4>
                                {sellerName && <p><span>Name:</span> {sellerName}</p>}
                                {sellerEmail && <p><span>Email:</span> {sellerEmail}</p>}
                                {sellerPhone && <p><span>Phone:</span> {sellerPhone}</p>}
                            </div>
                        )}

                        <p className="connect_text">Let's connect!</p>

                        <div className="social_row">
                            <a
                                href="https://www.facebook.com/share/17t1BQfnvW/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <FaFacebookF />
                            </a>
                            <a
                                href="https://www.instagram.com/importerr_/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <FaInstagram />
                            </a>
                            <a
                                href="https://www.linkedin.com/company/importerr/"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <FaLinkedinIn />
                            </a>
                        </div>
                    </div>


                    <div className="thankyou_right_column">
                        <div className="order_summary_box">
                            <h3 className="order_summary_title">Order Details</h3>

                            <div className="order_details_section">
                                <div className="order_detail_row">
                                    <span className="detail_label">Order ID</span>
                                    <span className="detail_value">{orderId}</span>
                                </div>

                                <div className="order_detail_row">
                                    <span className="detail_label">Placed On</span>
                                    <span className="detail_value">
                                        {new Date().toLocaleString('en-IN', {
                                            day: '2-digit', month: 'short', year: 'numeric',
                                            hour: '2-digit', minute: '2-digit'
                                        })}
                                    </span>
                                </div>
                                <div className="order_detail_row">
                                    <span className="detail_label">Payment Method</span>
                                    <span className="detail_value payment_method">
                                        {paymentMethod === 'razorpay' ? 'Razorpay' :
                                            paymentMethod === 'wallet' ? 'IM Cash' :
                                                paymentMethod === 'bank_transfer' ? 'Bank Transfer' :
                                                    paymentMethod === 'ccavenue' ? 'CCAvenue' :
                                                        (paymentMethod || '—')}
                                    </span>
                                </div>
                            </div>
                            {/* <div className="divider"></div>
                            <div className={`order_items_section ${products.length > 3 ? 'order_items_scroll' : ''}`}>
                                {products.map((item, idx) => (
                                    <div key={`${item.id}-${idx}`} className="order_item">
                                        <div className="item_image_container">
                                            <img
                                                src={item.imageUrl}
                                                alt={item.name}
                                                className="item_image"
                                                onError={(e) => {
                                                    e.currentTarget.src =
                                                        "https://via.placeholder.com/50x50/f0f0f0/666666?text=No+Image";
                                                }}
                                            />
                                        </div>
                                        <div className="item_details">
                                            <span className="item_name" title={item.name}>
                                                {truncate(item.name, 25)}
                                            </span>

                                            <div className="item_meta">
                                            
                                                <span className="item_quantity">{item.quantity}x</span>
                                             
                                                <span className="item_price">{formatINR(item.amount)}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>

                           
                            <div className="divider"></div>

                            <div className="totals_section">
                                <div className="total_row">
                                    <span className="total_label">Subtotal</span>
                                    <span className="total_value">
                                        {subtotal !== null ? formatINR(subtotal) : formatINR(grandTotal)}
                                    </span>
                                </div>

                                <div className="total_row">
                                    <span className="total_label">GST (included)</span>
                                    <span className="total_value">
                                        {totalGst !== null ? formatINR(totalGst) : formatINR(0)}
                                    </span>
                                </div>

                                <div className="total_row">
                                    <span className="total_label">Discount</span>
                                    <span className="total_value">{formatINR(discount)}</span>
                                </div>

                                <div className="total_row final_total">
                                    <span className="total_label">Total</span>
                                    <span className="total_value final_total_value">{formatINR(grandTotal)}</span>
                                </div>
                            </div> */}
                        </div>


                        <div className="thankyou_btn_group">
                            <button className="primary_btn" onClick={() => navigate("/")}>
                                Continue Shopping
                            </button>
                            <button className="outline_btn" onClick={() => navigate("/seller/orders")}>
                                Track Your Order
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
